// SettingsPlanCheckoutCard.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

// Services
import {
    createWorkspaceSetupIntent,
    createWorkspaceStripeSubscription,
} from 'services/billing/WorkspaceSubscriptionWriteService';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#FFFFFF',
            fontSize: '15px',
            '::placeholder': {
                color: '#8A8A8A',
            },
        },
        invalid: {
            color: '#FF8F8F',
        },
    },
};

function getInitialBillingEmail({ billingRecord, user }) {
    return String(
        billingRecord?.billingEmail
        || user?.data_profile?.email
        || user?.email
        || ''
    ).trim();
}

function getInitialBillingName({ billingRecord, user }) {
    return String(
        billingRecord?.billingName
        || user?.data_profile?.name
        || user?.title
        || ''
    ).trim();
}

/**
 * SettingsPlanCheckoutCard
 *
 * Renders a Stripe card checkout form for a workspace paid plan.
 *
 * @param {object} billingRecord - Current workspace billing record.
 * @param {object} plan - Selected paid plan.
 * @param {object} theme - Active theme tokens.
 * @param {object} user - Active user model.
 * @param {object} workspace - Active workspace model.
 * @param {function} onCancel - Called when checkout is dismissed.
 * @param {function} onSuccess - Called after successful subscription creation.
 *
 * @returns {ReactNode} The rendered checkout card.
 */
const SettingsPlanCheckoutCard = React.memo(({
    billingRecord,
    plan,
    theme,
    user,
    workspace,
    onCancel,
    onSuccess,
}) => {

    // ----- HOOKS ----- //

    // Stripe instance from the shared Elements provider.
    const stripe = useStripe();

    // Stripe Elements registry for the mounted payment fields.
    const elements = useElements();

    // ----- STATE ----- //

    // Billing email draft state.
    const [billingEmail, setBillingEmail] = useState(() => getInitialBillingEmail({ billingRecord, user }));

    // Billing name draft state.
    const [billingName, setBillingName] = useState(() => getInitialBillingName({ billingRecord, user }));

    // Checkout pending state.
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Checkout error state.
    const [errorMessage, setErrorMessage] = useState('');

    // ----- MEMOS ----- //

    // Derived billing email from current billing/user state.
    const initialBillingEmail = useMemo(() => {
        return getInitialBillingEmail({ billingRecord, user });
    }, [billingRecord, user]);

    // Derived billing name from current billing/user state.
    const initialBillingName = useMemo(() => {
        return getInitialBillingName({ billingRecord, user });
    }, [billingRecord, user]);

    // Whether the checkout form has the minimum data needed to submit.
    const canSubmit = useMemo(() => {
        return Boolean(
            stripe
            && elements
            && !isSubmitting
            && String(billingEmail || '').trim()
            && String(billingName || '').trim()
            && String(plan?.stripePriceId || '').trim()
            && String(plan?.key || '').trim()
            && String(workspace?.key || '').trim()
        );
    }, [billingEmail, billingName, elements, isSubmitting, plan?.key, plan?.stripePriceId, stripe, workspace?.key]);

    // ----- EFFECTS ----- //

    // Keeps billing details in sync with record and user context changes.
    useEffect(() => {
        setBillingEmail(initialBillingEmail);
    }, [initialBillingEmail]);

    // Keeps billing contact name in sync with record and user context changes.
    useEffect(() => {
        setBillingName(initialBillingName);
    }, [initialBillingName]);

    // ----- HANDLERS ----- //

    /**
     * Creates a setup intent, confirms the card, and creates the subscription.
     */
    const handleSubscribe = async () => {
        if (!canSubmit) return;

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setErrorMessage('Payment form is not ready yet.');
            return;
        }

        setErrorMessage('');
        setIsSubmitting(true);

        try {
            const setupIntentData = await createWorkspaceSetupIntent({
                workspaceKey: workspace.key,
                email: String(billingEmail || '').trim(),
                name: String(billingName || '').trim(),
            });

            const clientSecret = String(setupIntentData?.clientSecret || '').trim();
            if (!clientSecret) {
                throw new Error('Stripe setup intent did not return a client secret.');
            }

            const setupResult = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    billing_details: {
                        email: String(billingEmail || '').trim(),
                        name: String(billingName || '').trim(),
                    },
                    card: cardElement,
                },
            });

            if (setupResult.error) {
                throw new Error(setupResult.error.message || 'Unable to confirm payment method.');
            }

            const paymentMethod = setupResult?.setupIntent?.payment_method;
            const paymentMethodId = typeof paymentMethod === 'string'
                ? paymentMethod
                : String(paymentMethod?.id || '').trim();

            if (!paymentMethodId) {
                throw new Error('Stripe setup intent did not return a payment method.');
            }

            const subscriptionResult = await createWorkspaceStripeSubscription({
                workspaceKey: workspace.key,
                email: String(billingEmail || '').trim(),
                name: String(billingName || '').trim(),
                paymentMethodId,
                planId: String(plan.stripePriceId || '').trim(),
                planKey: String(plan.key || '').trim(),
                userKey: user?.key || '',
            });

            onSuccess && onSuccess({
                billingEmail: String(billingEmail || '').trim(),
                billingName: String(billingName || '').trim(),
                subscriptionResult,
            });
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to subscribe to this workspace plan.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <div
            className="settings-modal-plan-checkout-card"
            style={{
                borderColor: theme.backgroundHover,
            }}
        >
            <div className="settings-modal-plan-checkout-header">
                <div
                    className="settings-modal-plan-checkout-title"
                    style={{
                        color: theme.foreground,
                    }}
                >
                    Subscribe To {plan?.title}
                </div>
                <div
                    className="settings-modal-plan-checkout-subtitle"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    This workspace will be billed per seat using the current member count.
                </div>
            </div>

            <div className="settings-modal-plan-checkout-fields">
                <TextInput
                    helperText="Billing contact email"
                    placeholder="name@company.com"
                    value={billingEmail}
                    width="100%"
                    onChange={(event) => setBillingEmail(event.target.value)}
                />

                <TextInput
                    helperText="Billing contact name"
                    placeholder="Billing contact"
                    value={billingName}
                    width="100%"
                    onChange={(event) => setBillingName(event.target.value)}
                />

                <div
                    className="settings-modal-plan-card-element"
                    style={{
                        borderColor: theme.backgroundHover,
                    }}
                >
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
            </div>

            <div className="settings-modal-plan-checkout-actions">
                <Button
                    label="Cancel"
                    onClick={onCancel}
                />

                <Button
                    label={isSubmitting ? 'Subscribing...' : 'Confirm Subscription'}
                    enabled={canSubmit}
                    onClick={handleSubscribe}
                />
            </div>

            {errorMessage && (
                <div className="settings-modal-error">{errorMessage}</div>
            )}
        </div>
    );
});

// Dev Tools
SettingsPlanCheckoutCard.displayName = 'SettingsPlanCheckoutCard';

export default SettingsPlanCheckoutCard;